import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { toast } from 'sonner';
import axios from 'axios';
import { Ticket, Calendar, MapPin, Clock, Loader2, QrCode, ArrowLeft, Sparkles, KeyRound, Mail } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function MyTickets() {
  const navigate = useNavigate();
  const { user, token } = useAuth();
  const [groups, setGroups] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expandedTicket, setExpandedTicket] = useState(null);
  const [setupSent, setSetupSent] = useState(false);
  const [setupSending, setSetupSending] = useState(false);

  useEffect(() => { fetchTickets(); }, []);

  const fetchTickets = async () => {
    try {
      const res = await axios.get(`${API}/my-tickets`, { headers: { Authorization: `Bearer ${token}` } });
      setGroups(res.data.ticket_groups || []);
    } catch { toast.error('Failed to load tickets'); }
    finally { setLoading(false); }
  };

  // Magic-link recipients (comp tickets, auto-provisioned wallet accounts) have
  // no password set. Offer them a one-click "email me a setup link" so they
  // can sign in directly next visit instead of hunting through inboxes.
  const sendPasswordSetup = async () => {
    if (!user?.email) return;
    setSetupSending(true);
    try {
      await axios.post(`${API}/auth/forgot-password`, { email: user.email });
      setSetupSent(true);
      toast.success(`Setup link sent to ${user.email}`);
    } catch {
      toast.error('Could not send setup link — try again in a moment');
    } finally {
      setSetupSending(false);
    }
  };

  if (loading) return <div className="min-h-screen bg-background flex items-center justify-center pt-20"><Loader2 className="h-8 w-8 animate-spin text-indigo-500" /></div>;

  return (
    <div className="min-h-screen bg-background pt-20 pb-12">
      <div className="max-w-2xl mx-auto px-4">
        <div className="flex items-center gap-3 mb-6">
          <Button variant="ghost" size="sm" onClick={() => navigate(-1)}><ArrowLeft className="h-4 w-4 mr-1" /> Back</Button>
          <div>
            <h1 className="font-heading text-2xl font-bold text-foreground flex items-center gap-2">
              <Ticket className="h-6 w-6 text-indigo-500" /> My Tickets
            </h1>
            <p className="text-sm text-muted-foreground">{groups.reduce((sum, g) => sum + g.tickets.length, 0)} tickets</p>
          </div>
        </div>

        {/* Magic-link / passwordless account → invite to set a password so
            they can sign in directly next time instead of hunting for the
            wallet email link. */}
        {user && user.has_password === false && (
          <Card className="bg-gradient-to-br from-indigo-500/10 to-purple-500/10 border-indigo-500/30 mb-5" data-testid="set-password-banner">
            <CardContent className="p-4 flex items-start gap-3">
              <div className="rounded-lg bg-indigo-500/15 p-2 flex-shrink-0">
                <KeyRound className="h-5 w-5 text-indigo-300" />
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-sm font-semibold text-foreground">Make returning faster — set a password</p>
                <p className="text-xs text-muted-foreground mt-0.5">
                  Right now you sign in via the magic link we email you. Set a password and you can hit <span className="font-mono">eventism.com/login</span> any time to see your tickets in one click.
                </p>
                {setupSent ? (
                  <p className="text-xs text-emerald-400 mt-2 flex items-center gap-1.5" data-testid="set-password-sent">
                    <Mail className="h-3.5 w-3.5" /> Check {user.email} for the setup link (valid 24h).
                  </p>
                ) : (
                  <Button
                    size="sm"
                    className="mt-2 bg-indigo-600 hover:bg-indigo-700 text-white h-8 text-xs"
                    onClick={sendPasswordSetup}
                    disabled={setupSending}
                    data-testid="set-password-cta"
                  >
                    {setupSending ? <Loader2 className="h-3 w-3 animate-spin mr-1.5" /> : <Mail className="h-3 w-3 mr-1.5" />}
                    Email me a setup link
                  </Button>
                )}
              </div>
            </CardContent>
          </Card>
        )}

        {groups.length === 0 ? (
          <Card className="bg-card border-border">
            <CardContent className="p-12 text-center">
              <Ticket className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
              <p className="text-lg font-semibold mb-1">No tickets yet</p>
              <p className="text-sm text-muted-foreground mb-4">Browse upcoming events and get your tickets!</p>
              <Button onClick={() => navigate('/events')}>Browse Events</Button>
            </CardContent>
          </Card>
        ) : (
          <div className="space-y-6">
            {groups.map((group, gi) => {
              const event = group.event || {};
              const isPast = event.event_date && new Date(event.event_date) < new Date();
              return (
                <div key={gi}>
                  <Card className={`bg-card border-border ${isPast ? 'opacity-60' : ''}`}>
                    <CardContent className="p-4">
                      <div className="flex items-start justify-between mb-3">
                        <div>
                          <h2 className="font-semibold text-lg text-foreground">{event.title || event.name || 'Event'}</h2>
                          <div className="flex flex-wrap gap-3 mt-1 text-xs text-muted-foreground">
                            {event.event_date && <span className="flex items-center gap-1"><Calendar className="h-3 w-3" /> {event.event_date}</span>}
                            {event.event_time && <span className="flex items-center gap-1"><Clock className="h-3 w-3" /> {event.event_time}</span>}
                            {event.venue_name && <span className="flex items-center gap-1"><MapPin className="h-3 w-3" /> {event.venue_name}</span>}
                          </div>
                        </div>
                        {isPast && <Badge className="bg-muted text-muted-foreground text-[10px]">Past Event</Badge>}
                      </div>

                      {/* Open Digital Wallet CTA */}
                      {group.order?.order_id && group.order?.magic_token && !isPast && (
                        <Button
                          asChild
                          className="w-full mb-3 bg-gradient-to-r from-indigo-600 via-purple-600 to-pink-600 hover:opacity-90 text-white font-semibold"
                          data-testid={`open-wallet-${group.order.order_id}`}
                        >
                          <a href={`/wallet/${group.order.order_id}?t=${group.order.magic_token}`}>
                            <Sparkles className="h-4 w-4 mr-2" /> Open Digital Wallet
                          </a>
                        </Button>
                      )}

                      <div className="space-y-2">
                        {group.tickets.map(ticket => (
                          <div key={ticket.ticket_id} className="border border-border rounded-lg overflow-hidden">
                            <button
                              className="w-full flex items-center gap-3 p-3 hover:bg-muted/30 transition-colors text-left"
                              onClick={() => setExpandedTicket(expandedTicket === ticket.ticket_id ? null : ticket.ticket_id)}
                              data-testid={`ticket-row-${ticket.ticket_code}`}
                            >
                              <QrCode className="h-5 w-5 text-indigo-400 flex-shrink-0" />
                              <div className="flex-1 min-w-0">
                                <p className="text-sm font-medium">{ticket.ticket_type_name}</p>
                                <p className="text-[10px] text-muted-foreground font-mono">{ticket.ticket_code}</p>
                              </div>
                              <Badge className={
                                ticket.status === 'checked_in' ? 'bg-emerald-500/20 text-emerald-400' :
                                ticket.status === 'cancelled' ? 'bg-red-500/20 text-red-400' :
                                'bg-indigo-500/20 text-indigo-400'
                              }>
                                {ticket.status === 'checked_in' ? 'Checked In' : ticket.status === 'cancelled' ? 'Cancelled' : 'Valid'}
                              </Badge>
                            </button>

                            {expandedTicket === ticket.ticket_id && (
                              <div className="p-4 bg-white border-t border-border flex flex-col items-center">
                                <img src={`data:image/png;base64,${ticket.qr_code_base64}`} alt="QR" className="w-48 h-48 mb-2" />
                                <p className="font-mono text-lg font-bold text-indigo-600">{ticket.ticket_code}</p>
                                <p className="text-xs text-muted-foreground mt-1">Present at the door for check-in</p>
                              </div>
                            )}
                          </div>
                        ))}
                      </div>
                    </CardContent>
                  </Card>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}
